import React, {useCallback, useRef} from 'react';
import styled from 'styled-components';
import {Item, ItemType} from 'shared/components/Product/Item';
import {useItemColumn} from 'shared/components/Product/ItemColumn';
import {CategoryContainer} from 'shared/components/Product/Categories';
import {SettingItem} from 'shared/components/Product/SettingItem';
import {
    DropProps,
    withDropTarget,
} from 'shared/components/DragAndDrop/withDropTarget';
import {useProductContext} from 'contexts';
import {useInView} from 'react-intersection-observer';
import {mobilePortrait} from 'shared/Breakpoints';
import {useIsTouchDevices} from 'shared/helpers/DeviceSize';

const SCROLL_OFFSET = 40;

const getScrollParent = (node: HTMLElement | null): HTMLElement | null => {
    if (!node) return null;

    const {overflowY} = window.getComputedStyle(node);
    const isScrollable = overflowY === 'auto' || overflowY === 'scroll';

    if (isScrollable && node.scrollHeight > node.clientHeight) {
        return node;
    }

    return getScrollParent(node.parentElement);
};

export interface ItemsType extends DropProps<ItemType> {
    items?: ItemType[];
    isProduct?: boolean;
    setError?: (errors: string[]) => void;
    noHr?: boolean;
    title?: string;
    loader?: boolean;
    noItemText?: string;
    showScrollButton?: boolean;
    marginBottom?: boolean;
}

export const Items = ({
    items,
    isProduct,
    setError,
    noHr = false,
    title,
    loader,
    noItemText = 'No Items',
    isDnD,
    findItem,
    moveItem,
    showScrollButton = true,
    marginBottom = true,
}: ItemsType) => {
    const {loaders} = useItemColumn(loader ?? false);
    const {isMiniBrowser} = useProductContext();
    const isTouchDevices = useIsTouchDevices();

    const {ref: inViewRef, inView} = useInView({
        threshold: isMiniBrowser || isTouchDevices ? 0.015 : 0.06,
        triggerOnce: false,
    });
    const containerRef = useRef<HTMLDivElement | null>(null);

    const setContainerRef = useCallback(
        (node: HTMLDivElement | null) => {
            containerRef.current = node;
            inViewRef(node);
        },
        [inViewRef]
    );

    const scrollToTop = useCallback(() => {
        const node = containerRef.current;
        if (!node) return;

        const scrollParent = getScrollParent(node.parentElement);
        const nodeTop = node.getBoundingClientRect().top;

        if (!scrollParent) {
            window.scrollTo({
                top: nodeTop + window.scrollY - SCROLL_OFFSET,
                behavior: 'smooth',
            });
            return;
        }

        const parentTop = scrollParent.getBoundingClientRect().top;
        const top =
            nodeTop - parentTop + scrollParent.scrollTop - SCROLL_OFFSET;
        scrollParent.scrollTo({top, behavior: 'smooth'});
    }, []);

    return (
        <>
            {showScrollButton && !inView ? (
                <ScrollButtonContainer $isMiniBrowser={isMiniBrowser ?? false}>
                    <button onClick={scrollToTop}>
                        Or scroll to all {(items && items.length) ?? 0} products
                        &#8964;
                    </button>
                </ScrollButtonContainer>
            ) : null}
            {!noHr ? <hr /> : null}
            {title ? <h2 className="recently-added-header">{title}</h2> : null}
            <CategoryContainer
                id="products-container"
                ref={setContainerRef}
                $miniBrowser={isMiniBrowser}
                $marginBottom={marginBottom}
                $fixed={
                    items
                        ? isMiniBrowser
                            ? items.length <= 1
                            : items.length < 5
                        : false
                }
                className="hardwareItems"
                style={title ? {marginTop: '0'} : {}}>
                {loaders}
                {items && items.length && !loader ? (
                    items.map((item) => {
                        if (isDnD) {
                            return (
                                <SettingItem
                                    key={item.id}
                                    draggable={item}
                                    item={item}
                                    moveItem={moveItem}
                                    findItem={findItem}
                                />
                            );
                        }

                        return (
                            <Item
                                key={item.id}
                                item={item}
                                isProduct={isProduct ?? false}
                                setError={setError}
                                loader={loader}
                            />
                        );
                    })
                ) : (
                    <NoItemWrapper>{noItemText}</NoItemWrapper>
                )}
            </CategoryContainer>
        </>
    );
};

export const ItemsDnd = withDropTarget(Items);

const NoItemWrapper = styled.div`
    text-align: center;
    padding-top: 20px;
    padding-bottom: 20px;
    font-weight: bold;
`;

const ScrollButtonContainer = styled.div<{$isMiniBrowser: boolean}>`
    position: fixed;
    bottom: 20px;
    left: ${({$isMiniBrowser}) => ($isMiniBrowser ? '170px' : '0')};
    right: ${({$isMiniBrowser}) => ($isMiniBrowser ? 'initial' : '0')};
    z-index: 99;
    color: white;
    text-align: center;

    > button {
        background: ${({theme}) => theme.colors.primary?.main};
        color: white;
        font-weight: 500;
        font-size: 0.85em;
        padding: 5px 15px;
        border: 0;
        border-radius: 22px;
    }

    ${mobilePortrait`
        bottom: 142px;
    `}
`;
